import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, ActivatedRoute } from "@angular/router";
import { Subscription } from 'rxjs';

import { VersionsViewService } from '@app/services/app/versions.service';
import { ProjectsService } from '@app/services/app/projects.service';
import { AppService } from '@app/services/app/app.service';
import * as Objects from '@core/objects';
import { TitleService } from '@core/services/title.service';
// models
import { VersionShare, UploadVersionShare, VersionShareType,UploadVersionStatusCodeEnum } from '@core/models/version-share.model';
import * as fromState from '@app/store/state/versions/versions.state';

@Component({
    selector: 'project-version-upload-request',
    templateUrl: './version-upload-request.component.html',
    styleUrls: ['./version-upload-request.component.scss']
})
export class ProjectDetailsVersionUploadRequestComponent implements OnInit, OnDestroy {
    public readonly UPLOAD_STATUS = UploadVersionStatusCodeEnum;
    public readonly SHARE_TYPE = VersionShareType;
    // for view upload result    
    public readonly VIEW_UPLOAD_VERSION_NONE = 0;
    public readonly VIEW_UPLOAD_VERSION_DONE = 1;
    public readonly VIEW_UPLOAD_VERSION_ERROR = 2;
  
    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_ERROR = 2;
    public readonly STATUS_NOT_FOUND= 3;
    public readonly STATUS_NOT_ALLOWED= 4;

    public readonly PLUGIN_SHARE_FEEDBACK = 'share_feedback';

    public version: Objects.Version = null;
    public project: Objects.Request = null;
    public projectHeader: Objects.BasicRequest = null;
    public originProject: Objects.Request = null;
    public bcTitle: string = 'UPLOAD VERSION';
    public excludedLabels: Array<Objects.Label> = [];
    public userVersionShare: VersionShare = null;
    public currentVersionShare: UploadVersionShare = null;
    public breadcrumbs: any = [{
        link: "/",
        title: 'Artwork v. . . .  <small>(#  .  .  . )</small>'
    }];
    public context: string = '';
    public status:number = this.STATUS_LOADING;
    public versionStatus: Objects.Status = null;
    public uploading: boolean = false;
    public submitted: boolean = false;
    public filesError: boolean = false;
    public files: Objects.FileMetadata [] = []; 
    public view: number = this.VIEW_UPLOAD_VERSION_NONE;
    public modalUploadErrorText = 'Something went wrong. Please try again. If the error persists, please reach out to our support team through the <strong>Help Me button</strong>.';

    public accessFeedbacks: boolean = false;
    public versionSharesList:   Array<VersionShare> = null;

    private user: Objects.User = null;

    protected vUploadSubs = new Subscription();
    
    
    constructor(
        public app: AppService,
        private title: TitleService,
        private projectsSrv: ProjectsService,
        private viewVersionsSrv: VersionsViewService,
        private router: Router,
        private activeRoute: ActivatedRoute,
    ) { }

    ngOnInit() {
        this.setDefaultBredcrumb();
        // set temporal header title
        this.title.set("Version...");
        this.vUploadSubs.add(this.viewVersionsSrv.selectVersionShares().subscribe((allShares:VersionShare[]) => {
            if(allShares != null){
                this.versionSharesList = allShares;
                this.loadUserVersionShare();
            }   

        }));

        this.vUploadSubs.add(this.viewVersionsSrv.selectCurrentUploadVersionShare().subscribe((uvShare: UploadVersionShare) => {
            if(uvShare != null){
                this.versionStatus = uvShare.origin.status;
                this.currentVersionShare = uvShare;
            }
            this.initView();
        }));

        this.vUploadSubs.add(this.projectsSrv.selectProject().subscribe((project: Objects.Request) => {
            this.project = project;
            this.initView();
        }));
        
        this.vUploadSubs.add(this.viewVersionsSrv.selectProject().subscribe((projectOrigin: Objects.Request) => {
            this.originProject = projectOrigin;
            this.initView();
        }));

        this.vUploadSubs.add(this.projectsSrv.selectProjectHeader().subscribe((header: Objects.Request) => {
            if (header == null) return;
            this.projectHeader = header;
            this.initView()
        }));

        this.vUploadSubs.add(this.viewVersionsSrv.selectVersion().subscribe((version: Objects.Version) =>
        {
            if(version != null ){ 
                this.version = version;
            }
            this.initView();
        }));

        // subscriber for upload status in store
        this.vUploadSubs.add(this.viewVersionsSrv.selectSendVersionFilesActionStatus().subscribe((status: fromState.VersionsActionStatus) => {
            if(status.success){
                this.view = this.VIEW_UPLOAD_VERSION_DONE;
            }
            if (status.error != null){
                if (status.error.status == 406){
                    this.filesError = true;
                    this.modalUploadErrorText = 'There are some fields you need to review before sent the artwork.';
                    this.view = this.VIEW_UPLOAD_VERSION_ERROR;
                } else {
                    this.view = this.VIEW_UPLOAD_VERSION_ERROR;
                }
            }
        }));

        this.vUploadSubs.add(this.activeRoute.params.subscribe((params: any) => {
            this.viewVersionsSrv.load(+params.vid);
        }));
        // plugins & add project from session
        this.vUploadSubs.add(this.app.getSession().subscribe( (session: any) => {
            this.accessFeedbacks = session.options.plugins.some((pa) => pa == this.PLUGIN_SHARE_FEEDBACK);
            this.user = session.current.user;
            // need to check access to load feedbacks
            if(!this.accessFeedbacks){
                this.status = this.STATUS_NOT_ALLOWED;
            } else {
                this.loadUserVersionShare();
            }
            this.initView();
        }));

        this.vUploadSubs.add(this.viewVersionsSrv.selectError('load_version').subscribe((error: HttpErrorResponse) => {
            this.setError(error)
        }));
    }

    ngOnDestroy() {
        this.viewVersionsSrv.resetUploadVersionFilesActionStatus();
        this.vUploadSubs.unsubscribe();
    }

    isVersionReady() : boolean {
        return this.files.length > 0 && !this.uploading && !this.submitted; 
    }

    upload(files: Objects.FileMetadata[]): void {
        this.files = files;
    }

    onCancel() : void {
        this.router.navigate([`/projects/${this.originProject.id}/versions/${this.version.id}`]);
    
    }
    
    onNewVersionSentDone(){
        this.view = this.VIEW_UPLOAD_VERSION_NONE;
        this.loadUserVersionShare();
    }

    onNewVersionSentError(){
        this.loadUserVersionShare();
        this.submitted = false;
        this.view = this.VIEW_UPLOAD_VERSION_NONE;
    }

    public onSubmit(): void {
        if(!this.isVersionReady()){
            return
        }
        this.submitted = true;
        this.filesError = false;

        let filesList = this.files.map((f: Objects.FileMetadata) => f.uuid)
        // console.log(" FILES to submit: ", filesList);
        this.viewVersionsSrv.sendNewVersionFiles(this.userVersionShare.entry.token, filesList);
    }


    onSetLabels(labels: Array<Objects.Label>){
        this.viewVersionsSrv.setLabels(labels);
    }

    protected setDefaultBredcrumb(){
        this.breadcrumbs[0] = {link: '',
                    title: 'Project <small>(# .  .  . )</small>'};
        this.breadcrumbs[1] = {link: "",
                    title: 'ARTWORK v. . . . <small>(# .  .  . )</small>'};

    }

    private initView() {
        if((this.project == null && this.projectHeader == null) || this.version == null || this.userVersionShare == null || this.currentVersionShare == null || this.user == null || this.status == this.STATUS_NOT_ALLOWED)
            return;
        this.setHeaderTitle();
        this.updateBreadcrumbs();
        this.status = this.STATUS_DONE;
    }

    private setHeaderTitle() {
        this.title.set("Upload Version  - Artwork #"+ this.version.id + "- Project #" + (this.project?.id ?? this.projectHeader?.id));

    }

    protected updateBreadcrumbs(){ // update intermediate breadcrumb depending on context
        const currentProject = this.project ?? this.projectHeader;
        switch (this.context) {
            case "origin": // by now, origin will be on its original project url and version
                // this.excludeLabels = this.originProject.labels;
                this.excludedLabels = this.version.labels.filter((vLabel: Objects.Label) =>
                    this.originProject.labels.some((pLabel: Objects.Label) => pLabel.id === vLabel.id)
                );
                this.breadcrumbs[0] = {link: '/projects/'+this.originProject.id,
                    title: 'Project <small>(#'+this.originProject.id+ ')</small>'};
                this.breadcrumbs[1] = {link: "/versions/"+this.version.id,
                    title: 'Artwork v.'+this.version.version+' <small>(#'+this.version.id+')</small>'};
                break;
            default:
                // this.excludeLabels = this.project.labels;
                this.excludedLabels = this.version.labels.filter((vLabel: Objects.Label) =>
                    currentProject.labels.some((pLabel: Objects.Label) => pLabel.id === vLabel.id)
                );
                this.breadcrumbs[0] = {link: '/projects/'+currentProject.id,
                                        title: 'Project <small>(#'+currentProject.id+ ')</small>'};
                this.breadcrumbs[1] = {link: '/projects/'+currentProject.id+"/versions/"+this.version.id,
                    title: 'Artwork v.'+this.version.version+' <small>(#'+this.version.id+')</small>'};
                break;
        }
    }

    protected loadUserVersionShare(){
        if (this.versionSharesList == null || this.user == null ){
            return;
        }
        // separate my last upload-version 
        this.userVersionShare = this.versionSharesList //.find((vs:VersionShare) => vs.type == this.SHARE_TYPE.UPLOAD_VERSION_TYPE && vs.entry.user.id == this.user.id);
            .filter((vs: VersionShare) => vs.type === this.SHARE_TYPE.UPLOAD_VERSION_TYPE && vs.entry.user.id === this.user.id)
            .sort((a, b) => new Date(b.status.date).getTime() - new Date(a.status.date).getTime())
            .find((upload: VersionShare) => upload.entry?.token !== '');
        if(this.userVersionShare && this.userVersionShare.entry?.token != '' ){
            this.viewVersionsSrv.loadUploadVersionShares(this.userVersionShare.entry.token);
        } else {
            this.status = this.STATUS_NOT_ALLOWED;
        }
        this.initView();
    }


    protected setError(error: HttpErrorResponse) {
        if(error == null)
            return;

        switch(error.status) {
            case 500:
                this.status = this.STATUS_ERROR;
                break;
            case 404:
                this.status = this.STATUS_NOT_FOUND;
                break;
            case 406:
                this.status = this.STATUS_NOT_ALLOWED;
                break;  
            case 403:
                this.status = this.STATUS_NOT_ALLOWED;
                break;
        }
    }


}